import React, { FC, useEffect, useRef, useState } from 'react';
import { Icons } from '../../atoms/Icon';
import * as SC from './styled';
type Content = {
  title: string;
  anchor: string;
  level: number;
};
export type TableOfContentsProps = MinimalSeoBlock & {
  className?: string;
  title: string;
  contents: (Content & {
    child?: Content[];
  })[];
};
type ListItemProps = {
  content: TableOfContentsProps['contents'][0];
  index: number;
  formatIndex: (index: number) => string;
};
const ListItem: FC<ListItemProps> = ({
  content,
  index,
  formatIndex
}) => <SC.Item key={index} $level={content.level}>
    <SC.ItemLink href={`#${content.anchor}`}>
      <SC.ItemLeft>
        <SC.ItemCount>{formatIndex(index)}. </SC.ItemCount>
        <SC.ItemText>{content.title}</SC.ItemText>
      </SC.ItemLeft>
      <SC.ItemIcon icon={Icons.chevronRight} />
    </SC.ItemLink>
    {content.child && <SC.List type="a" $isChild>
        {content.child.map((child, index) => <ListItem key={index} content={child} index={index} formatIndex={i => String.fromCharCode(97 + i)} />)}
      </SC.List>}
  </SC.Item>;
const TableOfContents: FC<TableOfContentsProps> = ({
  className,
  title,
  contents
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const current = containerRef.current;
    const observer = new IntersectionObserver(([entry]) => {
      setIsSticky(!entry.isIntersecting);
      setIsCollapsed(!entry.isIntersecting);
    }, {
      root: null,
      threshold: 0
    });
    if (current) {
      observer.observe(current);
    }
    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, []);
  return <SC.Box>
      <SC.Skeleton ref={containerRef} />
      <SC.StickyWrapper $isSticky={isSticky}>
        <SC.Container className={className} $isSticky={isSticky}>
          <SC.Header onClick={() => setIsCollapsed(!isCollapsed)} role="button">
            <SC.Title>{title}</SC.Title>
            <SC.Icon icon={isCollapsed ? Icons.plus : Icons.minus} />
          </SC.Header>
          <SC.Content $isCollapsed={isCollapsed}>
            <SC.List type="1">
              {contents.map((content, index) => <ListItem key={index} content={content} index={index} formatIndex={i => (i + 1).toString()} />)}
            </SC.List>
          </SC.Content>
        </SC.Container>
        <SC.RightContent $isSticky={isSticky} />
      </SC.StickyWrapper>
    </SC.Box>;
};
export default TableOfContents;